import React from 'react';
import { Box } from '@mui/material';
import { StatCard } from './shared';

/**
 * Top-level summary cards showing total generation, revenue, and avg price.
 *
 * Avg price is **generation-weighted** (`Σ revenue / Σ MWh`), not an
 * arithmetic mean of regional averages — the prior version was silently
 * wrong for any multi-region portfolio (light-load region prices got the
 * same weight as heavy-load ones). Matches the convention used by the
 * `/overview.kpis.weighted_capture_aud_per_mwh` server field, so the agent
 * and this card surface the same number for the same window.
 */
const SummaryCards = ({ generation, revenue, price }) => {
  const totalGen = (generation || []).reduce((sum, g) => sum + (g.value_mwh || 0), 0);
  const totalRev = (revenue || []).reduce((sum, r) => sum + (r.revenue_aud || 0), 0);

  // Gen-weighted average — fall back to arithmetic mean of regional avgs
  // only when we have no generation data (defensive; tile shows a placeholder
  // anyway in that case).
  let avgPrice;
  if (totalGen > 0) {
    avgPrice = totalRev / totalGen;
  } else if (price && price.length > 0) {
    avgPrice = price.reduce((s, p) => s + (p.avg_price || 0), 0) / price.length;
  } else {
    avgPrice = 0;
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
      <StatCard
        label="Total Generation"
        value={totalGen >= 1000 ? (totalGen / 1000).toFixed(1) : totalGen.toFixed(1)}
        unit={totalGen >= 1000 ? 'GWh' : 'MWh'}
        color="#D98A2B"
      />
      <StatCard
        label="Total Revenue"
        value={totalRev >= 1_000_000 ? (totalRev / 1_000_000).toFixed(2) : totalRev >= 1000 ? (totalRev / 1000).toFixed(1) : totalRev.toFixed(0)}
        unit={totalRev >= 1_000_000 ? 'M AUD' : totalRev >= 1000 ? 'K AUD' : 'AUD'}
        color="#3d8cf5"
      />
      <StatCard
        label="Avg Price"
        value={avgPrice.toFixed(2)}
        unit="A$/MWh"
        color="#f59e0b"
      />
    </Box>
  );
};

export default SummaryCards;
